import { useEffect, useRef } from 'react';
import useDeepCompareMemoize from './useDeepCompareMemoize';

/**
 * Runs an effect only on dependency updates, skipping the initial mount and comparing dependencies deeply instead of by reference. Guards the first run with an isInitialMount ref, memoizes the dependency list through useDeepCompareMemoize and returns cleanup as the effect teardown.
 *
 * @param callback - Effect body invoked on every update after the first.
 * @param dependencies - Values deep-compared to decide whether to re-run.
 * @param cleanup - Teardown returned from the effect.
 * @returns Nothing.
 */
const useUpdateDeepCompareEffect = (
  callback: (...args: any[]) => any,
  dependencies: any[] = [],
  cleanup?: any,
) => {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      callback();
    }
    return cleanup;
  }, useDeepCompareMemoize(dependencies));
};

export default useUpdateDeepCompareEffect;
